import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, ErrorSummary, Input, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  quoteDeskService,
  tatState,
  type DeskFilters,
  type DeskListRow,
  type DeskSettings,
  type TatState,
} from '@/services/insurerQuoteDesk';
import { useAuthStore } from '@/store';
import { formatCurrency, formatDateTime } from '@/utils';

/**
 * Insurer portal > Quote Desk (#399). The queue of quote requests a broker or
 * POSP flagged "needs insurer pricing" for THIS insurer, most urgent first.
 * Scoped server-side; the tiles are filters over the same list.
 */

// eslint-disable-next-line react-refresh/only-export-components
export const QUOTE_DESK_QUERY_KEY = ['insurer-quote-desk'];

const TILES: { key: string; label: string; filter: DeskFilters; count: (s: Record<string, number>) => number }[] = [
  { key: 'open', label: 'Open', filter: { status: 'open' }, count: (s) => s.open },
  { key: 'in_review', label: 'In review', filter: { status: 'in_review' }, count: (s) => s.in_review },
  { key: 'info', label: 'Waiting on broker', filter: { status: 'info_requested' }, count: (s) => s.info_requested },
  { key: 'overdue', label: 'Overdue', filter: { overdue: true }, count: (s) => s.overdue },
  { key: 'unassigned', label: 'Unassigned', filter: { assigned: 'unassigned' }, count: (s) => s.unassigned },
];

/** Requests that have left the queue: shown in the History view. */
const HISTORY_STATUSES = 'approved,declined,withdrawn,expired';

const TAT_CLASSES: Record<TatState['kind'], string> = {
  normal: 'text-gray-700 dark:text-gray-300',
  soon: 'text-amber-700 dark:text-amber-400 font-medium',
  overdue: '',
  paused: 'text-gray-500 dark:text-gray-400',
  closed: 'text-gray-400',
};

// eslint-disable-next-line react-refresh/only-export-components
export function TatBadge({ row }: { row: Pick<DeskListRow, 'status' | 'due_at' | 'is_overdue' | 'time_left_seconds'> }) {
  const tat = tatState(row);
  if (tat.kind === 'overdue') {
    return (
      <Badge variant="error" size="sm">
        {tat.label}
      </Badge>
    );
  }
  return <span className={`text-sm ${TAT_CLASSES[tat.kind]}`}>{tat.label}</span>;
}

function money(value: string | null) {
  return value == null ? '—' : formatCurrency(Number(value));
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: [...QUOTE_DESK_QUERY_KEY, 'settings'], queryFn: quoteDeskService.settings });
  const [draft, setDraft] = useState<Partial<DeskSettings>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const save = useMutation({
    mutationFn: () => quoteDeskService.saveSettings(draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUOTE_DESK_QUERY_KEY });
      toast.success('Quote Desk settings saved');
      onClose();
    },
    onError: (err) => setErrors([getErrorMessage(err)]),
  });
  const value = { ...settings.data, ...draft } as DeskSettings;
  return (
    <Modal isOpen onClose={onClose} title="Desk settings" size="md">
      {settings.isLoading ? (
        <LoadingSpinner />
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setErrors([]);
            save.mutate();
          }}
        >
          <ErrorSummary messages={errors} />
          <label className="mb-4 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={Boolean(value.quote_desk_enabled)}
              onChange={(e) => setDraft((d) => ({ ...d, quote_desk_enabled: e.target.checked }))}
            />
            Accept pricing requests from brokers
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              id="desk-tat"
              label="Response time (hours)"
              type="number"
              min={1}
              max={720}
              value={value.quote_tat_hours ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, quote_tat_hours: Number(e.target.value) }))}
            />
            <Input
              id="desk-limit"
              label="Adjustment limit (%)"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={value.quote_adjust_limit_pct ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, quote_adjust_limit_pct: e.target.value }))}
            />
            <Input
              id="desk-validity"
              label="Quote validity (days)"
              type="number"
              min={1}
              max={90}
              value={value.quote_validity_days ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, quote_validity_days: Number(e.target.value) }))}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            A new response time applies to requests flagged from now on, never to ones already in the queue.
          </p>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={save.isPending}>
              Save
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  );
}

export function QuoteDeskPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'insurer_admin';
  const [filters, setFilters] = useState<DeskFilters>({});
  const [activeTile, setActiveTile] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [view, setView] = useState<'active' | 'history'>('active');
  const [page, setPage] = useState(1);
  const history = view === 'history';

  // A new filter or view starts again from the first page.
  useEffect(() => {
    setPage(1);
  }, [filters, view]);

  const summary = useQuery({
    queryKey: [...QUOTE_DESK_QUERY_KEY, 'summary'],
    queryFn: quoteDeskService.summary,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const list = useQuery({
    queryKey: [...QUOTE_DESK_QUERY_KEY, 'list', view, filters, page],
    queryFn: () =>
      quoteDeskService.list(
        history
          ? { ...filters, overdue: undefined, status: HISTORY_STATUSES, ordering: '-submitted_at', page }
          : { ...filters, page }
      ),
    refetchInterval: 60_000,
  });

  const counts = (summary.data ?? {}) as unknown as Record<string, number>;
  const rows = list.data?.results ?? [];

  return (
    <div>
      <PageHeader
        title="Quote Desk"
        subtitle="Quote requests brokers have sent you to price"
        actions={
          isAdmin ? (
            <Button variant="outline" onClick={() => setShowSettings(true)}>
              Desk settings
            </Button>
          ) : undefined
        }
      />

      <div className="mb-3 flex gap-2" role="group" aria-label="Queue view">
        {(
          [
            ['active', 'In the queue'],
            ['history', 'History (approved, declined, closed)'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={view === key}
            onClick={() => {
              setView(key);
              setActiveTile(null);
              setFilters({ search: filters.search });
            }}
            className={`rounded-full border px-3 py-1 text-sm ${
              view === key
                ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={`mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5 ${history ? 'hidden' : ''}`}>
        {TILES.map((tile) => (
          <button
            key={tile.key}
            type="button"
            aria-pressed={activeTile === tile.key}
            onClick={() => {
              const next = activeTile === tile.key ? null : tile.key;
              setActiveTile(next);
              setFilters(next ? { ...tile.filter, search: filters.search } : { search: filters.search });
            }}
            className={`rounded-lg border p-3 text-left ${
              activeTile === tile.key
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
            }`}
          >
            <div className="text-xs text-gray-500 dark:text-gray-400">{tile.label}</div>
            <div
              className={`text-2xl font-semibold ${
                tile.key === 'overdue' && (counts.overdue ?? 0) > 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {summary.data ? tile.count(counts) : '—'}
            </div>
          </button>
        ))}
      </div>

      <Card padding="none">
        <form
          className="flex flex-wrap items-end gap-3 border-b border-gray-200 p-4 dark:border-gray-700"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters((f) => ({ ...f, search }));
          }}
        >
          <div className="w-full sm:w-72">
            <Input
              id="desk-search"
              label="Search"
              placeholder="Request no. or insured name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            aria-label="Assigned"
            className="appearance-none select-chevron rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            value={filters.assigned ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, assigned: e.target.value || undefined }))}
          >
            <option value="">Anyone</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <select
            aria-label="Requested by"
            className="appearance-none select-chevron rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            value={filters.requester_type ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, requester_type: e.target.value || undefined }))}
          >
            <option value="">Broker or POSP</option>
            <option value="broker">Broker</option>
            <option value="posp">POSP</option>
          </select>
          <Button type="submit" variant="outline">
            Search
          </Button>
        </form>

        {list.isLoading ? (
          <div className="p-6">
            <LoadingSpinner />
          </div>
        ) : list.isError ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">{getErrorMessage(list.error)}</p>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {summary.data && !summary.data.desk_enabled ? (
              <>
                The Quote Desk is switched off for your company.
                {isAdmin && (
                  <>
                    {' '}
                    <button type="button" className="text-primary-600 underline" onClick={() => setShowSettings(true)}>
                      Open desk settings
                    </button>
                  </>
                )}
              </>
            ) : (
              history
                ? 'No finished quote requests yet.'
                : 'No quote requests need your pricing.'
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" aria-label="Quote requests">
              <thead>
                <tr>
                  {['Request', 'Line', 'Requested by', 'Insured', 'IDV / Sum insured', 'Reference', 'Submitted', history ? 'Decided' : 'Response time', 'Status', 'Assigned to'].map(
                    (heading) => (
                      <th
                        key={heading}
                        className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((row) => (
                  <tr key={row.id} className="text-sm text-gray-700 dark:text-gray-300">
                    <td className="px-4 py-2">
                      <Link to={`/insurance/quote-desk/${row.id}`} className="font-medium text-primary-600 hover:underline">
                        {row.request_number}
                      </Link>
                    </td>
                    <td className="px-4 py-2">{row.insurance_type.name}</td>
                    <td className="px-4 py-2">
                      {row.broker.name}{' '}
                      <Badge size="sm" variant={row.requester_type === 'posp' ? 'info' : 'default'}>
                        {row.requester_type === 'posp' ? 'POSP' : 'Broker'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">{row.proposer_name}</td>
                    <td className="px-4 py-2">{money(row.key_figure)}</td>
                    <td className="px-4 py-2">{money(row.reference_premium)}</td>
                    <td className="px-4 py-2">{formatDateTime(row.submitted_at)}</td>
                    <td className="px-4 py-2">
                      {history ? (
                        row.decided_at ? formatDateTime(row.decided_at) : '—'
                      ) : (
                        <TatBadge row={row} />
                      )}
                    </td>
                    <td className="px-4 py-2">{row.status_display}</td>
                    <td className="px-4 py-2">{row.assigned_to?.name ?? 'Unassigned'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(list.data?.total_pages ?? 1) > 1 && (
          <nav
            aria-label="Pagination"
            className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700"
          >
            <span className="text-gray-500 dark:text-gray-400">
              Page {list.data?.current_page ?? page} of {list.data?.total_pages} ({list.data?.count} requests)
            </span>
            <span className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!list.data?.previous} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Button size="sm" variant="outline" disabled={!list.data?.next} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </span>
          </nav>
        )}
      </Card>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}

export default QuoteDeskPage;
